import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import AppScrollArea from '../../components/common/AppScrollArea';
import { resolvePersistedChartTypeLabel } from '../statistics/charts/board/lib/statisticsPersistedCharts';
import {
	resolveDashboardChartBindingLabel,
	resolveDashboardChartRefreshLabel,
} from '../statistics/charts/board/lib/statisticsChartRefreshMode';

const RECENT_LIMIT = 5;
const MODAL_CONTENT_SELECTOR = '.home-dashboard-charts-fullscreen-modal__content';

const KIND_META = {
	折线: { icon: '↗', bg: 'rgba(99,102,241,0.15)', text: '#818cf8' },
	柱状: { icon: '▮', bg: 'rgba(99,102,241,0.15)', text: '#818cf8' },
	条形: { icon: '▬', bg: 'rgba(99,102,241,0.15)', text: '#818cf8' },
	饼图: { icon: '◕', bg: 'rgba(6,182,212,0.15)', text: '#22d3ee' },
	环形: { icon: '◯', bg: 'rgba(6,182,212,0.15)', text: '#22d3ee' },
	漏斗: { icon: '▽', bg: 'rgba(6,182,212,0.15)', text: '#22d3ee' },
	地图: { icon: '▣', bg: 'rgba(34,211,238,0.15)', text: '#22d3ee' },
	超期: { icon: '⚠', bg: 'rgba(239,68,68,0.15)', text: '#f87171' },
	流程: { icon: '▤', bg: 'rgba(245,158,11,0.15)', text: '#fbbf24' },
	看板: { icon: '▣', bg: 'rgba(99,102,241,0.15)', text: '#818cf8' },
};

const KIND_META_FALLBACK = { icon: '▣', bg: 'rgba(148,163,184,0.15)', text: '#94a3b8' };

const KIND_LABEL_ORDER = ['折线', '柱状', '条形', '饼图', '环形', '漏斗', '地图', '超期', '流程', '看板'];

export const resolveCatalogKindLabel = (item) => {
	if (item?.source === 'live-preset') return '看板';
	if (item?.snapshot) return resolvePersistedChartTypeLabel(item.snapshot);
	return '柱状';
};

const resolveItemDescription = (item) => {
	if (!item?.snapshot) return item?.title ?? '';
	const binding = resolveDashboardChartBindingLabel(item.snapshot).short;
	const refresh = resolveDashboardChartRefreshLabel(item.snapshot).short;
	return `${binding} · ${refresh}`;
};

const itemMatchesQuery = (item, query) => {
	if (!query) return true;
	const haystack = [item.title, resolveCatalogKindLabel(item), resolveItemDescription(item)].join(' ').toLowerCase();
	return haystack.includes(query);
};

const DashboardChartCommandPalette = ({ items = [], activeId = null, onSelect, recentIds = [] }) => {
	const [open, setOpen] = useState(false);
	const [query, setQuery] = useState('');
	const [kindFilter, setKindFilter] = useState('all');
	const [highlightIndex, setHighlightIndex] = useState(0);
	const inputRef = useRef(null);
	const listRef = useRef(null);
	const rootRef = useRef(null);
	const activeIdRef = useRef(activeId);
	const flatItemsRef = useRef([]);
	const [portalTarget, setPortalTarget] = useState(null);
	const activeItem = items.find((item) => item.id === activeId) ?? items[0] ?? null;
	const canSwitch = items.length > 1;
	const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/i.test(navigator.platform);
	const kindMeta = KIND_META[resolveCatalogKindLabel(activeItem)] ?? KIND_META_FALLBACK;
	const kindFilters = useMemo(() => {
		const present = new Set(items.map((item) => resolveCatalogKindLabel(item)));
		return [{ id: 'all', label: '全部' }, ...KIND_LABEL_ORDER.filter((id) => present.has(id)).map((id) => ({ id, label: id }))];
	}, [items]);

	useEffect(() => {
		if (!open) {
			setPortalTarget(null);
			return;
		}
		const content = rootRef.current?.closest(MODAL_CONTENT_SELECTOR) ?? document.querySelector(MODAL_CONTENT_SELECTOR);
		setPortalTarget(content);
	}, [open]);

	const filtered = useMemo(() => {
		const needle = query.trim().toLowerCase();
		return items.filter((item) => {
			const kind = resolveCatalogKindLabel(item);
			if (kindFilter !== 'all' && kind !== kindFilter) return false;
			return itemMatchesQuery(item, needle);
		});
	}, [items, kindFilter, query]);

	const groups = useMemo(() => {
		const needle = query.trim();
		if (needle || kindFilter !== 'all') {
			return [{ label: `${filtered.length} 个结果`, items: filtered }];
		}
		const seen = new Set();
		const recentItems = recentIds
			.map((id) => items.find((item) => item.id === id))
			.filter((item) => {
				if (!item || item.id === activeId || seen.has(item.id)) return false;
				seen.add(item.id);
				return true;
			})
			.slice(0, RECENT_LIMIT);
		const recentIdSet = new Set(recentItems.map((item) => item.id));
		const rest = items.filter((item) => !recentIdSet.has(item.id));
		const byKind = KIND_LABEL_ORDER
			.map((label) => ({
				label,
				items: rest.filter((item) => resolveCatalogKindLabel(item) === label),
			}))
			.filter((group) => group.items.length > 0);
		return [...(recentItems.length ? [{ label: '最近查看', items: recentItems }] : []), ...byKind];
	}, [activeId, filtered, items, kindFilter, query, recentIds]);

	const flatItems = useMemo(() => groups.flatMap((group) => group.items), [groups]);
	activeIdRef.current = activeId;
	flatItemsRef.current = flatItems;

	const closePalette = useCallback(() => {
		setOpen(false);
		setQuery('');
		setKindFilter('all');
		setHighlightIndex(0);
	}, []);

	const selectItem = useCallback(
		(item) => {
			if (!item) return;
			onSelect?.(item.id);
			closePalette();
		},
		[closePalette, onSelect],
	);

	useEffect(() => {
		if (!open) return undefined;
		setHighlightIndex(Math.max(0, flatItemsRef.current.findIndex((item) => item.id === activeIdRef.current)));
		const frame = window.requestAnimationFrame(() => inputRef.current?.focus());
		return () => window.cancelAnimationFrame(frame);
	}, [open]);

	useEffect(() => {
		setHighlightIndex(0);
	}, [kindFilter, query]);

	useEffect(() => {
		if (!open) return;
		const node = listRef.current?.querySelector(`[data-idx="${highlightIndex}"]`);
		node?.scrollIntoView({ block: 'nearest' });
	}, [highlightIndex, open]);

	useEffect(() => {
		if (!canSwitch) return undefined;
		const onKeyDown = (event) => {
			const isOpenShortcut = (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k';
			if (isOpenShortcut) {
				event.preventDefault();
				event.stopPropagation();
				setOpen((current) => !current);
				return;
			}
			if (!open) return;
			if (event.key === 'Escape') {
				event.preventDefault();
				event.stopPropagation();
				closePalette();
				return;
			}
			if (event.key === 'ArrowDown') {
				event.preventDefault();
				setHighlightIndex((index) => Math.min(index + 1, Math.max(flatItems.length - 1, 0)));
				return;
			}
			if (event.key === 'ArrowUp') {
				event.preventDefault();
				setHighlightIndex((index) => Math.max(index - 1, 0));
				return;
			}
			if (event.key === 'Enter') {
				event.preventDefault();
				selectItem(flatItems[highlightIndex]);
			}
		};
		document.addEventListener('keydown', onKeyDown, true);
		return () => document.removeEventListener('keydown', onKeyDown, true);
	}, [canSwitch, closePalette, flatItems, highlightIndex, open, selectItem]);

	if (!activeItem) return null;

	let flatIdx = 0;

	return (
		<div ref={rootRef} className='hdm-make-switch-wrap'>
			{canSwitch ? (
				<button
					type='button'
					className={['hdm-make-switch', open ? 'is-open' : null].filter(Boolean).join(' ')}
					aria-haspopup='dialog'
					aria-expanded={open}
					onClick={() => setOpen(true)}
				>
					<span className='hdm-make-switch__icon' aria-hidden='true'>
						{kindMeta.icon}
					</span>
					<span className='hdm-make-switch__copy'>
						<span className='hdm-make-switch__title'>{activeItem.title}</span>
						<span className='hdm-make-switch__sub'>
							{items.findIndex((item) => item.id === activeId) + 1} / {items.length}
							<span className='hdm-make-switch__kind'>{resolveCatalogKindLabel(activeItem)}</span>
						</span>
					</span>
					<span className='hdm-make-switch__keys' aria-hidden='true'>
						<span className='hdm-make-kbd'>{isMac ? '⌘' : 'Ctrl'}</span>
						<span className='hdm-make-kbd'>K</span>
					</span>
				</button>
			) : (
				<div className='hdm-make-switch hdm-make-switch--static'>
					<span className='hdm-make-switch__icon' aria-hidden='true'>
						{kindMeta.icon}
					</span>
					<span className='hdm-make-switch__copy'>
						<span className='hdm-make-switch__title'>{activeItem.title}</span>
					</span>
				</div>
			)}

			{open && portalTarget
				? createPortal(
						<div className='hdm-make-palette'>
							<button type='button' className='hdm-make-palette__backdrop' aria-label='关闭切换图表' onClick={closePalette} />
							<div className='hdm-make-palette__panel' role='dialog' aria-label='切换图表' onClick={(event) => event.stopPropagation()}>
								<div className='hdm-make-palette__search-row'>
									<span className='hdm-make-palette__search-mark' aria-hidden='true'>
										⌕
									</span>
									<input
										ref={inputRef}
										className='hdm-make-palette__search'
										type='search'
										value={query}
										placeholder='搜索图表名称、类型或口径…'
										aria-label='搜索图表'
										onChange={(event) => setQuery(event.target.value)}
									/>
									{query ? (
										<button type='button' className='hdm-make-palette__clear' onClick={() => setQuery('')}>
											清除
										</button>
									) : (
										<span className='hdm-make-palette__search-keys'>
											<span className='hdm-make-kbd'>{isMac ? '⌘' : 'Ctrl'}</span>
											<span className='hdm-make-kbd'>K</span>
										</span>
									)}
								</div>

								<div className='hdm-make-palette__chips'>
									{kindFilters.map((option) => {
										const meta = option.id === 'all' ? null : KIND_META[option.id];
										const active = kindFilter === option.id;
										return (
											<button
												key={option.id}
												type='button'
												className={['hdm-make-palette__chip', active ? 'is-active' : null].filter(Boolean).join(' ')}
												style={active && meta ? { background: meta.bg, color: meta.text } : undefined}
												onClick={() => setKindFilter(option.id)}
											>
												{meta ? <span aria-hidden='true'>{meta.icon}</span> : null}
												{option.label}
											</button>
										);
									})}
									<span className='hdm-make-palette__count'>
										{filtered.length} / {items.length} 图表
									</span>
								</div>

								<AppScrollArea maxHeight='22rem' className='hdm-make-palette__list app-scroll-area--embedded'>
									<div ref={listRef} className='hdm-make-palette__list-inner'>
										{groups.map((group) => {
											if (!group.items.length) return null;
											const groupStart = flatIdx;
											flatIdx += group.items.length;
											return (
												<div key={group.label}>
													<p className='hdm-make-palette__group'>{group.label}</p>
													{group.items.map((item, index) => {
														const idx = groupStart + index;
														const kind = resolveCatalogKindLabel(item);
														const meta = KIND_META[kind] ?? KIND_META_FALLBACK;
														const isHighlight = idx === highlightIndex;
														const isActive = item.id === activeId;
														return (
															<button
																key={item.id}
																type='button'
																data-idx={idx}
																className={[
																	'hdm-make-palette__item',
																	isHighlight ? 'is-highlight' : null,
																	isActive ? 'is-current' : null,
																]
																	.filter(Boolean)
																	.join(' ')}
																onMouseEnter={() => setHighlightIndex(idx)}
																onClick={() => selectItem(item)}
															>
																<span
																	className='hdm-make-palette__item-icon'
																	style={{ background: isHighlight ? meta.bg : undefined, color: isHighlight ? meta.text : undefined }}
																>
																	{meta.icon}
																</span>
																<span className='hdm-make-palette__item-copy'>
																	<span className='hdm-make-palette__item-title'>
																		{item.title}
																		{isActive ? <span className='hdm-make-palette__now'>当前</span> : null}
																	</span>
																	<span className='hdm-make-palette__item-desc'>{resolveItemDescription(item)}</span>
																</span>
																<span className='hdm-make-palette__item-side'>
																	<span className='hdm-make-palette__type' style={{ background: meta.bg, color: meta.text }}>
																		{kind}
																	</span>
																	<span className='hdm-make-palette__mode'>
																		<span className='hdm-make-palette__mode-dot' />
																		{item.snapshot ? resolveDashboardChartRefreshLabel(item.snapshot).short : '实时'}
																	</span>
																</span>
															</button>
														);
													})}
												</div>
											);
										})}
										{flatItems.length === 0 ? (
											<div className='hdm-make-palette__empty'>
												<p>未找到匹配图表</p>
												<span>尝试搜索「环形」「折线」「定格」，或清除类型筛选</span>
											</div>
										) : null}
									</div>
								</AppScrollArea>

								<div className='hdm-make-palette__foot'>
									<span>
										<kbd className='hdm-make-kbd'>↑↓</kbd> 导航
									</span>
									<span>
										<kbd className='hdm-make-kbd'>↵</kbd> 打开
									</span>
									<span>
										<kbd className='hdm-make-kbd'>Esc</kbd> 关闭
									</span>
								</div>
							</div>
						</div>,
						portalTarget,
					)
				: null}
		</div>
	);
};

DashboardChartCommandPalette.propTypes = {
	items: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.string.isRequired,
			title: PropTypes.string.isRequired,
			source: PropTypes.string,
			snapshot: PropTypes.object,
		}),
	),
	activeId: PropTypes.string,
	onSelect: PropTypes.func,
	recentIds: PropTypes.arrayOf(PropTypes.string),
};

export default memo(DashboardChartCommandPalette);
